import React, { useMemo, useState } from 'react';
import {
  loadConnectAndInitialize,
  type StripeConnectInstance,
} from '@stripe/connect-js';
import {
  ConnectAccountOnboarding,
  ConnectComponentsProvider,
} from '@stripe/react-connect-js';
import { societyApi } from '@/features/societies/api/societyApi';
import env from '@/config/env';

interface StripeEmbeddedOnboardingProps {
  societyId: string;
  onExit: () => void;
}

/**
 * Composant embedded Stripe Connect pour finaliser l'onboarding d'un compte connecté.
 * Le `fetchClientSecret` ré-appelle l'API à chaque ouverture pour récupérer une session
 * fraîche. `disable_stripe_user_authentication: true` est posé côté serveur pour éviter
 * le challenge de login Stripe imposé aux comptes controller récents.
 *
 * Voir : https://docs.stripe.com/connect/supported-embedded-components/account-onboarding
 */
export const StripeEmbeddedOnboarding: React.FC<StripeEmbeddedOnboardingProps> = ({
  societyId,
  onExit,
}) => {
  const [initError, setInitError] = useState<string | null>(null);

  const connectInstance: StripeConnectInstance | null = useMemo(() => {
    try {
      // La clé publishable peut venir du backend (cas multi-mode) ou du config front
      // en fallback. Le backend reste la source de vérité.
      const fallbackKey = env.STRIPE_PUBLIC_KEY;
      if (!fallbackKey || fallbackKey === 'false') {
        setInitError("Clé Stripe non configurée pour ce domaine.");
        return null;
      }

      return loadConnectAndInitialize({
        publishableKey: fallbackKey,
        fetchClientSecret: async () => {
          const resp = await societyApi.getStripeAccountSession(societyId);
          if (!resp.success || !resp.clientSecret) {
            throw new Error(resp.error || 'Impossible de créer la session Stripe');
          }
          return resp.clientSecret;
        },
      });
    } catch (err: any) {
      setInitError(err?.message || 'Erreur d\'initialisation Stripe');
      return null;
    }
  }, [societyId]);

  if (initError) {
    return (
      <div className="tw-p-6 tw-text-center tw-text-red-700">
        <p>Erreur d'initialisation du composant Stripe&nbsp;:</p>
        <p className="tw-font-mono tw-text-sm tw-mt-2">{initError}</p>
      </div>
    );
  }

  if (!connectInstance) {
    return <div className="tw-p-6 tw-text-center">Chargement…</div>;
  }

  return (
    <ConnectComponentsProvider connectInstance={connectInstance}>
      {/*
        `fields: 'eventually_due'` force la collecte des champs sans deadline
        immédiate — notamment la pièce d'identité du représentant placée par
        Stripe en `requirements.eventually_due` après l'upload du KBIS via
        account_token. Sans ça, le composant ne demande que `currently_due`
        et le compte reste "Restricted" sans message d'erreur clair.

        `futureRequirements: 'include'` collecte en plus les exigences qui
        deviendront obligatoires bientôt (changements réglementaires Stripe).

        Restaure le comportement de l'ancien `accountLinks.create` qui
        utilisait `collection_options.fields: 'eventually_due'`.
      */}
      <ConnectAccountOnboarding
        onExit={onExit}
        collectionOptions={{
          fields: 'eventually_due',
          futureRequirements: 'include',
        }}
      />
    </ConnectComponentsProvider>
  );
};
